import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { Badge, Box, Card, CircularProgress, Stack, Theme, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { makeStyles } from '@mui/styles';
import dayjs from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useEffect, useRef, useState } from 'react';
import { useCheckBooleanSetting } from '../../../contexts/SettingsContext';
import useChartResize from '../../../hooks/useChartResize';
import { getDailerCountData, getIvrDialerCountV2, getWidgetConfig, outboundCampaignStatusLive } from '../../../services/api-service';
import { getLastFetchedDetailFromBrowser } from '../../../services/application-service';
import { ReactComponent as FilterAltOutlinedIcon } from '../../../static/icons/filter.svg';
import { ReactComponent as GraphIcon } from '../../../static/icons/graph.svg';
import { ReactComponent as RefreshOutlinedIcon } from '../../../static/icons/refresh.svg';
import { ReactComponent as TableIcon } from '../../../static/icons/table.svg';
import { aggregateDataByCampaignName, checkDataAccessFilters, checkRoles, determineFilterType } from '../../../utils/util';
import FilterDrawer from '../../FilterDrawer/FilterDrawer';
import OZTable from '../../OZTable/OZTable';
import WidgetDownload from '../../Widget-Download/Widget-Download';
dayjs.extend(utc);
dayjs.extend(tz);

const useStyles: any = makeStyles((theme: Theme) => ({
	typeButton: {
		padding: '5px !important'
	},
	filterItem: {
		fontSize: 12,
		height: 24,
		borderRadius: 20,
		background: 'rgba(0, 142, 255, 0.1)',
		border: '1px solid #008eff',
		padding: `0 ${theme.spacing(2)}`,
		display: 'flex',
		alignItems: 'center',
		gap: 8,
	},
	itemIndicator: {
		width: 5,
		height: 5,
		borderRadius: 1,
		display: 'inline-block',
		position: 'relative',
		// top: '-2px',
		// marginRight: 4
	},
	filterItemText: {
		// margin: 0,
		color: '#536580'
	},
	filterItemSubText: {
		color: '#99A0A8'
	}
}));

const tableColumns = [
	{ name: 'Campaign Name', key: 'CampaignName', align: 'left', sortable: true },
	{ name: 'Upload', key: 'TotalAdded', align: 'left', sortable: true },
	{ name: 'Sucess', key: 'TotalSuccess', align: 'left', sortable: true },
	{ name: 'Failed', key: 'TotalFailed', align: 'left', sortable: true },
	{ name: 'Fresh Lead', key: 'TotalFreshLead', align: 'left', sortable: true },
	{ name: 'Pending', key: 'TotalPending', align: 'left', sortable: true },
	{ name: 'Expired', key: 'TotalExpired', align: 'left', sortable: true },
	{ name: 'Limit Exceeded', key: 'DayLimitReached', align: 'left', sortable: true },
];

function OutboundCampaignStatus(props: any) {
	const classes = useStyles();
	const ivrDialerV2Enabled = useCheckBooleanSetting('IVR_DIALER_V2');
	const downloadContent = useRef<null | any>(null);
	// const chartElement: any = useRef(null);
	const [isDrawerOpen, setIsDrawerOpen] = useState<boolean | undefined>(false);
	const [outboundCampaignFilter, setOutboundCampaignFilter] = useState<any>(props?.selectedFilters?.filters || []);
	const [graphData, setGraphData] = useState<any>([]);
	const [rowData, setRowData] = useState<any[]>([]);
	const [columnData] = useState<any[]>(tableColumns);
	const [allowedFilters, setAllowedFilters] = useState<string[]>(['Campaign']);
	const [filterType, setFilterType] = useState<any>(props?.selectedFilters?.filterSection?.id || 1);
	const [filterSection, setFilterSection] = useState<any>(props?.selectedFilters?.filterSection || {});
	const [campaignName, setCampaignName] = useState<any>([]);
	const [responseLength, setresponseLength] = useState<Number>(0);
	const [lastFetchedTime, setLastFetchedTime] = useState<any>('');
	const [bound, setBound] = useState<any>('All');
	const [showGraphData, setShowGraphData] = useState<boolean>(true);
	const { chartElementRef, chartContainerRef, chartDimensions, redrawChart } = useChartResize();
	const [outboundCampaignStatusLoading, setOutboundCampaignStatusLoading] = useState<boolean>(false);
	const [campaignPosition, setCampaignPosition] = useState<string[]>(props?.widgetData?.campaignPosition || []);
	const additionalApiParams = { campaignType: 'Preview,Progressive,IVR,Predictive' };

	const handleBound = (event: React.MouseEvent<HTMLElement>, newAlignment: string | null) => {
		if (newAlignment === null || newAlignment === undefined) return;
		setBound(newAlignment);
	};
	const handleDownload = (type: string) => {
		console.log(type);
	};

	const toggleDrawer = (isOpen: boolean) => {
		setIsDrawerOpen(isOpen);
	};

	const toggleRefresh = () => {
		const filterIds = outboundCampaignFilter.length ? outboundCampaignFilter.map((item: any) => item.id).join(',') : '0';
		retrieveGraphData(filterIds, filterType, 'all', campaignPosition);
	};

	const clearFilter = () => {
		setOutboundCampaignFilter([]);
		setCampaignPosition([]);
		handleSaveCampaignPosition([]);
		props?.handleFilterChange?.([], filterSection, props?.widgetId);
	};

	const handleFilterDelete = (id: Number) => {
		const newFilter = outboundCampaignFilter.filter((item: any) => item.id !== id);
		setOutboundCampaignFilter(newFilter);
		props?.handleFilterChange?.(newFilter, filterSection, props?.widgetId);
	};

	const filterItems = outboundCampaignFilter.map((item: any, index: number) => (
		<div className={classes.filterItem}>
			<div>
				<div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
					<span className={classes.itemIndicator} style={{ background: item.color }}></span>
					<p className={classes.filterItemText}>
						{item.label}
					</p>
				</div>
				<b>{item.target}</b>
			</div>
			<CloseOutlinedIcon onClick={() => handleFilterDelete(item.id)} color="primary" style={{ cursor: 'pointer', fontSize: '14px' }} />
		</div>
	));

	const campaignPositionFilterItems = campaignPosition?.length > 0 ? campaignPosition.map((item: any, index: number) => (
		<div className={classes.filterItem}>
			<p className={classes.filterItemText}>{item}</p>
			<CloseOutlinedIcon onClick={() => handleCampaignPositionFilterDelete(item)} color="primary" style={{ cursor: 'pointer', fontSize: '14px' }} />
		</div>
	)) : null;

	const handleCampaignPositionFilterDelete = (item: any) => {
		const newCampaignPosition = campaignPosition.filter((_: any) => _ !== item);
		setCampaignPosition(newCampaignPosition);
		handleSaveCampaignPosition(newCampaignPosition);
	}

	const handleFilterExistingData = (filteredData: any) => {
		setOutboundCampaignFilter(filteredData);
		props?.handleFilterChange?.(filteredData, filterSection, props?.widgetId);
	};

	const handleApplyFilters = ({ selectedFilters, filterType: newFilterType, filterSection: newfilterSection, additionalFilters }: any) => {
		setOutboundCampaignFilter(selectedFilters);
		setFilterType(newFilterType);
		setFilterSection(newfilterSection);
		if (additionalFilters?.campaignPosition) {
			setCampaignPosition(additionalFilters.campaignPosition as string[]);
			handleSaveCampaignPosition(additionalFilters.campaignPosition as string[]);
		}
		props?.handleFilterChange?.(selectedFilters, newfilterSection, props?.widgetId);
		toggleDrawer(false);
	};

	const handleSaveCampaignPosition = (campaignPosition: string[]) => {
		if (props?.handleSaveDataToFilter && props?.widgetId) {
			props?.handleSaveDataToFilter({
				widgetId: props?.widgetId,
				value: {
					campaignPosition: campaignPosition
				}
			})
		}
	}

	const handleViewToggle = () => {
		setShowGraphData((prevState) => !prevState);
	};

	const retrieveGraphData = async (filters = '0', type = '1', campaiginType = 'all', campaignPositionFilter = ['All']) => {
		try {
			if (outboundCampaignStatusLoading) {
				console.log('Widget data fetch already in progress, skipping this cycle outboundCampaignStatusLoading');
				return;
			}
			setOutboundCampaignStatusLoading(true);
			let params: any = '';
			let apiResponse: any = '';
			const newStartTime = dayjs().tz(localStorage.getItem('userTZ') || '').startOf('day');
			const newEndTime = dayjs().tz(localStorage.getItem('userTZ') || '');
			const duration = {
				fromTime: newStartTime?.format('YYYY-MM-DD HH:mm:ss'),
				toTime: newEndTime?.format('YYYY-MM-DD HH:mm:ss')
			}
			if (checkRoles("ROLE_DATA_ACCESS")) {
				let campaignPosition = 'ALL'
				if (campaignPositionFilter && campaignPositionFilter.length > 0 && Array.isArray(campaignPositionFilter)) {
					campaignPosition = campaignPositionFilter.join(',');
				}
				// IVR Dialer V2: IVR campaigns are served by a separate endpoint (apiId 8001). When enabled,
				// use it alone for the IVR toggle, and merge it into the 1003 results for the All toggle.
				const ivrParams = { agent: '0', campaignName: filters, campaignPosition, toggle: '0', skill: '0', campaignType: 'IVR', ...duration };
				if (ivrDialerV2Enabled && bound === 'IVR') {
					apiResponse = await getIvrDialerCountV2({ ...ivrParams });
				} else {
					params = { agent: '0', campaignName: filters, campaignPosition, toggle: '0', skill: '0', CampaignType: bound, ...duration };
					apiResponse = await getDailerCountData({ ...params });
					if (ivrDialerV2Enabled && bound === 'All') {
						try {
							const ivrRows = await getIvrDialerCountV2({ ...ivrParams });
							apiResponse = [...(apiResponse || []), ...(Array.isArray(ivrRows) ? ivrRows : [])];
						} catch (e) {
							console.error(e);
						}
					}
				}
			} else {
				params = { campaign: filters, campaiginType, ...duration };
				apiResponse = await outboundCampaignStatusLive({ ...params });
			}
			apiResponse = aggregateDataByCampaignName(apiResponse);
			setRowData(apiResponse);
			// const params: any = { campaign: filters, campaiginType };
			// const apiResponse: any = await outboundCampaignStatusLive({ ...params });

			const uploadData = apiResponse.map((item: any) => ({
				y: +item.TotalAdded,
				...item
			}));
			const successData = apiResponse.map((item: any) => ({
				y: +item.TotalSuccess,
				...item
			}));
			const failedData = apiResponse.map((item: any) => ({
				y: +item.TotalFailed,
				...item
			}));
			const pendingData = apiResponse.map((item: any) => ({
				y: +item.TotalPending,
				...item
			}));
			const expiredData = apiResponse.map((item: any) => ({
				y: +item.TotalExpired,
				...item
			}));
			const dayLimitReached = apiResponse.map((item: any) => ({
				// 	y: +item.DayLimitReached,
				y: +item.DayLimitReached,
				...item
			}));
			const totalFreshLead = apiResponse.map((item: any) => ({
				y: +item.TotalFreshLead,
				...item
			}));
			const campaignName = apiResponse.map((item: any) => item.CampaignName);
			setCampaignName(campaignName);
			const graphOptions = [
				{
					name: 'Upload',
					color: '#E3ECFD',
					data: uploadData,
					pointWidth: 35,
					borderRadius: 5
				},
				{
					name: 'Success',
					color: '#4F9F52',
					data: successData,
					pointPlacement: -0.025
				},
				{
					name: 'Failed',
					color: '#E65B5C',
					data: failedData,
					pointPlacement: -0.27
				},
				{
					name: 'Fresh Lead',
					color: '#EBADDD',
					data: totalFreshLead,
					pointPlacement: -0.3
				},
				{
					name: 'Pending',
					color: '#E7AF5B',
					data: pendingData,
					pointPlacement: -0.3
				},
				{
					name: 'Expired',
					color: '#A026AD',
					data: expiredData,
					pointPlacement: -0.3
				},
				{
					name: 'Limit Exceeded',
					color: '#EBADAD',
					data: dayLimitReached,
					pointPlacement: -0.3
				},
			];

			setGraphData(graphOptions);
			setLastFetchedTime(getLastFetchedDetailFromBrowser());
			redrawChart();
		} catch (e) {
			console.error("error in retrieveGraphData outbound campaign status", e)
		} finally {
			setOutboundCampaignStatusLoading(false);
		}

	};

	useEffect(() => {
		const fetchWidgetConfig = async () => {
			try {
				if (checkDataAccessFilters()) {
					let widgetConfig = await getWidgetConfig('131', 'widget');
					if (widgetConfig) {
						if (typeof widgetConfig === 'string') {
							widgetConfig = JSON.parse(widgetConfig);
						}
						// Check the condition and set the appropriate filters
						const filters = widgetConfig.data && checkDataAccessFilters()
							? widgetConfig.data
							: widgetConfig.config;

						if (filters) {
							const filtersArray = filters.split(',');
							setAllowedFilters(filtersArray);
							const determinedFilterType = determineFilterType(filtersArray);
							if (determinedFilterType !== undefined) {
								setFilterType(determinedFilterType);
							}
						}
					}
				}
			} catch (error) {
				console.error('Error fetching widget config:', error);
			}
		};
		fetchWidgetConfig();
	}, []);

	useEffect(() => {
		const filterIds = (outboundCampaignFilter.length && !(responseLength === outboundCampaignFilter.length)) ? outboundCampaignFilter.map((item: any) => item.id).join(',') : '0';
		retrieveGraphData(filterIds, filterType, 'all', campaignPosition);
		// retrieveGraphData is intentionally excluded to avoid recreating the polling trigger on each render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterType, outboundCampaignFilter, bound, campaignPosition, responseLength, ivrDialerV2Enabled]);

	const options = {
		credits: { enabled: false },
		chart: {
			type: 'column',
			height: chartDimensions.height,
		},
		title: {
			text: ''
		},
		xAxis: {
			title: {
				text: 'Campaigns ➡',
				style: {
					fontWeight: 'bold'
				}
			},
			categories: campaignName,
			crosshair: true,
			labels: {
				rotation: -45,
				style: {
					fontSize: '12px',
					fontFamily: 'Roboto'
				}
			}
		},
		yAxis: {
			title: {
				text: 'Interaction Volume',
				style: { fontWeight: 'bold' }
			},
			gridLineDashStyle: 'dash'
		},
		legend: {
			layout: 'horizontal',
			align: 'center',
			verticalAlign: 'top'
			// itemMarginTop: 10
		},
		tooltip: {
			shared: true
		},
		plotOptions: {
			series: {
				cursor: 'pointer',
				pointWidth: 10,
				borderRadius: 2,
				groupPadding: 0.3
			}
		},
		series: graphData
	};

	const getRowData = (rows: any[]) => rows;

	const hasActiveFilters = outboundCampaignFilter.length > 0 || campaignPosition.length > 0;
	const containerHeight = hasActiveFilters ? 'calc(100% - 84px)' : 'calc(100% - 48px)';

	return (
		<>
			<Card ref={downloadContent} className="customCard">
				<div className="card-header">
					<div className="card-title">
						Outbound Campaign Status
						{outboundCampaignStatusLoading ? <CircularProgress size={20} sx={{ mx: 1 }} /> : <div className="refresh-btn card-icon" onClick={() => toggleRefresh()}>
							<RefreshOutlinedIcon fontSize="small" style={{ color: '#536580' }} />
						</div>}
						<span className="last-fetched-time" style={{ marginLeft: '6px' }}>Last updated at {lastFetchedTime} </span>
					</div>
					<div className="card-actions">
						{checkRoles("ROLE_DATA_ACCESS") &&
							<ToggleButtonGroup value={bound} color="primary" exclusive onChange={handleBound} aria-label="bound">
								<ToggleButton value="All" className={classes.boundButton} aria-label="All">
									<span>All</span>
								</ToggleButton>
								<ToggleButton value="Preview" className={classes.boundButton} aria-label="Preview">
									<span>Preview</span>
								</ToggleButton>
								<ToggleButton value="Progressive" className={classes.boundButton} aria-label="Progressive">
									<span>Progressive</span>
								</ToggleButton>
								<ToggleButton value="Predictive" className={classes.boundButton} aria-label="Predictive">
									<span>Predictive</span>
								</ToggleButton>
								<ToggleButton value="IVR" className={classes.boundButton} aria-label="IVR">
									<span>IVR</span>
								</ToggleButton>
							</ToggleButtonGroup>
						}
						<div className="card-icon" onClick={() => handleViewToggle()}>
							{showGraphData ? <TableIcon /> : <GraphIcon />}
						</div>
						<WidgetDownload
							xlsx={!showGraphData}
							rowData={getRowData(rowData)}
							columns={columnData}
							element={downloadContent}
							handleDownload={handleDownload}
							name={'Outbound Campaign Status'}
						></WidgetDownload>
						<div className="card-icon" onClick={() => toggleDrawer(true)}>
							<Badge badgeContent={outboundCampaignFilter.length + campaignPosition.length} color="primary">
								<FilterAltOutlinedIcon fontSize="small" style={{ color: '#536580' }} />
							</Badge>
						</div>
					</div>
				</div>
				<Box sx={{ p: 2, pb: 1, background: "#FFFFFF" }}>
					{hasActiveFilters && (
						<div className="filter-outer">
							<div className="filter-container">
								<Stack direction={'row'} spacing={1}>
									{outboundCampaignFilter.length > 0 ? filterItems : null}
									{campaignPosition.length > 0 ? campaignPositionFilterItems : null}
								</Stack>
							</div>
							<div className="filter-remove" onClick={clearFilter}>
								Remove
							</div>
						</div>
					)}
				</Box>
				<Box ref={chartContainerRef} sx={{ height: containerHeight, width: '100%' }}>
					{showGraphData ? (
						<HighchartsReact ref={chartElementRef} highcharts={Highcharts} options={options} />
					) : (
						<OZTable downloadContent={downloadContent} columns={columnData} rows={rowData} noMaxHeight={true} height={chartDimensions.height} />
					)}
				</Box>
			</Card>
			<FilterDrawer
				isOpen={isDrawerOpen}
				toggleDrawer={toggleDrawer}
				applyFilters={handleApplyFilters}
				existingSelectedFilters={outboundCampaignFilter}
				setResponseLength={setresponseLength}
				additionalApiParams={additionalApiParams}
				allowedFilters={allowedFilters}
				existingFilterCategory={filterType}
				applyFilterOnExistingData={handleFilterExistingData}
				showAdditionalFilters={checkRoles("ROLE_DATA_ACCESS") ? ['campaignPosition'] : []}
				initialAdditionalFilters={checkRoles("ROLE_DATA_ACCESS") ? { campaignPosition } : { campaignPosition: [] }}
			/>
			{/* <FilterDrawer isOpen={isDrawerOpen} toggleDrawer={toggleDrawer} applyFilters={handleApplyFilters} allowedFilters={['Campaign']} existingSelectedFilters={outboundCampaignFilter} setResponseLength={setresponseLength} additionalApiParams={additionalApiParams} existingFilterCategory={filterType} applyFilterOnExistingData={handleFilterExistingData}/> */}
		</>
	);
}

export default OutboundCampaignStatus;
